// Counterpart of webapp/src/store/views.ts

import {create} from 'zustand'
import {BoardView, createBoardView} from '../types/boardView'
import client from '../api/client'

type ViewsState = {
    views: Record<string, BoardView>
    loadViews: (boardId: string) => Promise<BoardView[]>
    upsertViews: (views: BoardView[]) => void
    upsertView: (view: BoardView) => void
    removeView: (viewId: string) => void
}

export const useViews = create<ViewsState>((set, get) => ({
    views: {},

    async loadViews(boardId) {
        const blocks = await client.getBlocksWithParent(boardId, 'view')
        const views = blocks.map((b) => createBoardView(b))
        get().upsertViews(views)
        return views
    },

    upsertViews(views) {
        set((state) => {
            const next = {...state.views}
            views.forEach((v) => {
                next[v.id] = v
            })
            return {views: next}
        })
    },

    upsertView(view) {
        get().upsertViews([view])
    },

    removeView(viewId) {
        set((state) => {
            const views = {...state.views}
            delete views[viewId]
            return {views}
        })
    },
}))

/** Upstream orders a board's views by createAt in the view tabs. */
export function selectViewsOfBoard(views: Record<string, BoardView>, boardId: string | undefined): BoardView[] {
    if (!boardId) {
        return []
    }
    return Object.values(views).
        filter((v) => v.boardId === boardId && v.deleteAt === 0).
        sort((a, b) => a.createAt - b.createAt)
}

export function selectActiveView(views: Record<string, BoardView>, boardId: string | undefined, viewId: string | undefined): BoardView | undefined {
    const boardViews = selectViewsOfBoard(views, boardId)
    if (viewId) {
        const found = boardViews.find((v) => v.id === viewId)
        if (found) {
            return found
        }
    }
    return boardViews[0]
}
